import { DeckDef } from "../types";

const T_BLOCKCHAIN = { term: "blockchain", gloss: "niezmienialny, publiczny rejestr" };
const T_HASH = { term: "hash", gloss: "cyfrowy odcisk głosu" };

// Deck sztandarowy „Pełny obraz" — wersja Extended dla Kancelarii / decydentów.
// Podzielony na 6 rozdziałów (slajdy-przekładki). Sceny z biblioteki (reuse jest tu pożądany).
export const extendedDeck: DeckDef = {
  id: "extended",
  slug: "pelny-obraz",
  title: "Pełny obraz (Extended)",
  subtitle: "Kompletna prezentacja dla decydentów — 6 rozdziałów",
  publisher: "Fundacja Potrafisz Polsko · lubbezposrednio.pl",
  estimatedMin: 35,
  ready: true,
  slides: [
    {
      id: "hero",
      tone: "brand",
      scene: "ballot-seal",
      logo: "/LBP_Logo_White.svg",
      title: "Wynik, którego nie da się *podważyć*",
      lead: "Wiarygodne głosowania online, zapieczętowane na blockchainie — pełny obraz w sześciu rozdziałach.",
      durationSec: 45,
      notes: "Otwarcie flagowe. Zapowiedz, że to kompletny obraz: od problemu, przez mechanizm, tajność, suwerenność, po zastosowania i roadmapę."
    },

    // ── Rozdział I ──
    { id: "div1", divider: true, tone: "brand", chapterNo: "I", title: "Dlaczego to *przełom*", lead: "Problem zaufania — i nasza odpowiedź.", notes: "Wejście w część o problemie i idei." },
    {
      id: "problem",
      scene: "black-box",
      kicker: "Problem",
      title: "Dziś trzeba *zaufać* operatorowi",
      lead: "A wynik łatwo podważyć — i trudno udowodnić, że był uczciwy.",
      bullets: [
        "Głosowania online opierają się na zaufaniu do firmy, która je prowadzi.",
        "Brak twardego, publicznego dowodu, że nikt nie podmienił głosów.",
        "Podważony wynik = podważona decyzja i autorytet instytucji."
      ],
      durationSec: 65,
      notes: "Problemem nie jest liczenie, tylko zaufanie i dowód. Metafora czarnej skrzynki."
    },
    {
      id: "idea",
      scene: "stamp-chain",
      kicker: "Nasza idea",
      title: "Uczciwość *zapieczętowana* na blockchainie",
      lead: "Każdy głos zostawia trwały, publiczny ślad — który każdy może niezależnie sprawdzić.",
      terms: [T_BLOCKCHAIN, { term: "hash", gloss: "cyfrowy odcisk" }],
      durationSec: 55,
      notes: "Sedno: blockchain jako nienaruszalna pieczęć na wyniku. Nie trzeba nam wierzyć — dowód jest publiczny."
    },
    {
      id: "filary",
      kicker: "Trzy filary",
      title: "Dlaczego to naprawdę *przełom*",
      pillars: [
        { title: "Niezmienialność", text: "Nikt — nawet my — nie dosypie ani nie podmieni głosu bez śladu.", icon: "FiLock" },
        { title: "Tajność udowodniona", text: "Wiadomo, ŻE ktoś zagłosował, ale nie wiadomo JAK (ZKP).", icon: "FiEyeOff" },
        { title: "Suwerenność", text: "Własny łańcuch Fundacji, otwarty kod, każdy może postawić węzeł.", icon: "FiShield" }
      ],
      durationSec: 85,
      notes: "Trzy filary, po jednym zdaniu. Serce pitchu."
    },

    // ── Rozdział II ──
    { id: "div2", divider: true, tone: "brand", chapterNo: "II", title: "Jak to *działa*", lead: "Od głosu do dowodu — krok po kroku.", notes: "Wejście w mechanizm." },
    {
      id: "overview",
      scene: "vote-flow",
      kicker: "W skrócie",
      title: "Od głosu do dowodu — w *czterech krokach*",
      lead: "Głos → cyfrowy odcisk → pieczęć na łańcuchu → publiczna weryfikacja.",
      terms: [{ term: "hash", gloss: "cyfrowy odcisk" }, T_BLOCKCHAIN],
      durationSec: 60,
      notes: "Mapa całości. Reszta rozdziału rozkłada każdy krok."
    },
    { id: "step-vote", scene: "step-vote", kicker: "Krok 1", title: "Oddajesz głos *z telefonu*", lead: "Wybierasz opcję w aplikacji lub przeglądarce — prosto, jak w bankowości.", durationSec: 45, notes: "Dla użytkownika to kilka kliknięć. Magia dzieje się dalej." },
    { id: "step-hash", scene: "step-hash", kicker: "Krok 2", title: "Głos dostaje *cyfrowy odcisk*", lead: "System tworzy z głosu unikalny „odcisk palca” (hash) — zmiana jednej litery daje zupełnie inny odcisk.", terms: [T_HASH], durationSec: 55, notes: "Metafora odcisku palca: najmniejsza zmiana = całkiem inny odcisk." },
    { id: "step-seal", scene: "step-seal", kicker: "Krok 3", title: "Odcisk trafia na *łańcuch*", lead: "Odcisk zostaje zapieczętowany w bloku łańcucha — trwale i bez możliwości cichej zmiany.", terms: [T_BLOCKCHAIN], durationSec: 55, notes: "Od tej chwili wyniku nie da się po cichu zmienić ani usunąć." },
    {
      id: "verify",
      scene: "verify-lights",
      kicker: "Krok 4",
      title: "Każdy sprawdzi, że *wynik się zgadza*",
      lead: "Potwierdzasz, że łączny wynik jest prawdziwy i nienaruszony — nie widząc, jak zagłosował ktokolwiek.",
      bullets: ["Sprawdzasz uczciwość wyniku, a nie pojedyncze głosy.", "Weryfikuje głosujący, dziennikarz i urząd — bez zaufania na słowo."],
      durationSec: 55,
      notes: "Domknięcie pętli zaufania: dowód publiczny, ale tajność głosu nienaruszona."
    },

    // ── Rozdział III ──
    { id: "div3", divider: true, tone: "brand", chapterNo: "III", title: "Tajność i *bezpieczeństwo*", lead: "Sekret głosu i odporność na manipulację.", notes: "Wejście w prywatność/ZKP." },
    {
      id: "paradox",
      scene: "paradox",
      kicker: "Pozorna sprzeczność",
      title: "Tajne, a jednak *sprawdzalne*",
      lead: "Zwykle coś jest albo tajne, albo weryfikowalne. My łączymy oba — dzięki kryptografii.",
      bullets: ["Tajność: nikt nie powiąże głosu z osobą.", "Weryfikowalność: każdy potwierdzi, że wynik jest prawdziwy."],
      terms: [{ term: "kryptografia", gloss: "matematyka zabezpieczeń" }],
      durationSec: 60,
      notes: "Fałszywy wybór: można mieć oba naraz."
    },
    { id: "zkp", scene: "zkp-witness", kicker: "ZKP", title: "Świadek, który *nie patrzy w kartę*", lead: "Dowód z wiedzą zerową potwierdza, że głos jest ważny i pojedynczy — nie ujawniając jego treści.", terms: [{ term: "ZKP", gloss: "dowód bez ujawniania" }], durationSec: 65, notes: "Świadek zaświadcza ważność, nie zaglądając do karty." },
    { id: "nullifier", scene: "nullifier", kicker: "Bez podwójnego głosu", title: "Jeden głos, *nie dwa*", lead: "Każdy uprawniony głosuje raz. Próba drugiego głosu jest odrzucana — bez ujawniania, kto próbował.", terms: [{ term: "nullifier", gloss: "znacznik „już zagłosowano”" }], durationSec: 55, notes: "Anonimowość bez chaosu: drugi głos zablokowany." },
    { id: "separation", scene: "separation", kicker: "Rozdział danych", title: "Kto głosował *≠* jak głosował", lead: "Lista uprawnionych i oddane głosy są trzymane osobno — nie istnieje mapowanie „osoba → wybór”.", durationSec: 55, notes: "Nawet my nie odtworzymy, kto jak zagłosował." },
    { id: "privacy", scene: "privacy-shield", kicker: "Prywatność", title: "Prywatność *z założenia*", lead: "Zbieramy minimum danych, szyfrujemy i przetwarzamy zgodnie z RODO — w Polsce i UE.", terms: [{ term: "privacy by design", gloss: "prywatność wbudowana" }, { term: "RODO", gloss: "unijna ochrona danych" }], durationSec: 55, notes: "Prywatność jako fundament, nie dodatek." },

    // ── Rozdział IV ──
    { id: "div4", divider: true, tone: "brand", chapterNo: "IV", title: "Suwerenność *cyfrowa*", lead: "Polskie, niezależne, otwarte.", notes: "Wejście w suwerenność." },
    {
      id: "foreign",
      scene: "foreign-dep",
      kicker: "Ryzyko",
      title: "Cudza technologia = *cudza kontrola*",
      lead: "Na zagranicznej, zamkniętej platformie państwo oddaje kontrolę i dane w obce ręce.",
      bullets: ["Zamknięty kod — nie wiadomo, co naprawdę robi system.", "Dane i serwery poza krajem, pod obcą jurysdykcją."],
      terms: [{ term: "vendor lock-in", gloss: "uzależnienie od dostawcy" }],
      durationSec: 60,
      notes: "Rzecz o kontroli nad krytyczną infrastrukturą, nie o pochodzeniu dostawcy."
    },
    { id: "own", scene: "own-chain", kicker: "Nasz fundament", title: "*Własny* łańcuch Fundacji", lead: "Rejestr wyników należy do polskiej Fundacji — nie do zagranicznej korporacji.", terms: [T_BLOCKCHAIN], durationSec: 50, notes: "Władamy łańcuchem, jego źródłem i autorytetem." },
    { id: "open", scene: "open-code", kicker: "Otwartość", title: "Otwarty kod — *każdy sprawdzi*", lead: "Kod węzła jest jawny. Niezależni eksperci mogą go zweryfikować — żadnych ukrytych mechanizmów.", terms: [{ term: "open source", gloss: "jawny, otwarty kod" }, { term: "audyt", gloss: "niezależna kontrola kodu" }], durationSec: 55, notes: "Brak czarnej skrzynki — przeciwieństwo zamkniętych rozwiązań." },
    { id: "nodes", scene: "node-cluster", kicker: "Niezależne węzły", title: "Trzy węzły, zasada *2-z-3*", lead: "Łańcuch utrzymują niezależne węzły. Żaden pojedynczy podmiot nie kontroluje wyniku — i każdy może postawić własny.", bullets: ["Wynik potwierdzają co najmniej 2 z 3 niezależnych węzłów.", "Urząd, uczelnia czy organizacja może uruchomić swój węzeł."], terms: [{ term: "węzeł", gloss: "niezależny serwer łańcucha" }, { term: "konsensus", gloss: "zgoda węzłów co do wyniku" }], durationSec: 70, notes: "Decentralizacja po polsku." },
    { id: "data", scene: "data-pl", kicker: "Dane", title: "Dane zostają *w Polsce i UE*", lead: "Przetwarzanie na terenie kraju i Unii — zgodnie z RODO, pod polską i unijną jurysdykcją.", terms: [{ term: "RODO", gloss: "unijna ochrona danych" }, { term: "jurysdykcja", gloss: "czyje prawo obowiązuje" }], durationSec: 50, notes: "Dane obywateli nie wyciekają poza UE ani pod obce prawo." },

    // ── Rozdział V ──
    { id: "div5", divider: true, tone: "brand", chapterNo: "V", title: "Zastosowania i *dowód*", lead: "Gdzie to działa i jak to udowadniamy.", notes: "Wejście w zastosowania." },
    {
      id: "admin",
      kicker: "Dla administracji",
      title: "Dla *administracji* i samorządów",
      pillars: [
        { title: "Budżet obywatelski", text: "Mieszkańcy wybierają projekty z telefonu — z weryfikowalnym wynikiem.", icon: "FiCheckCircle" },
        { title: "Konsultacje społeczne", text: "Realny głos mieszkańców, bez papieru i kolejek.", icon: "FiUsers" },
        { title: "Głosowania rad i uchwały", text: "Imienne lub tajne głosowania radnych — szybkie i udokumentowane.", icon: "FiShield" }
      ],
      durationSec: 75,
      notes: "Trzy znane administracji zastosowania. Można dołożyć referenda i głosowania w instytucjach."
    },
    {
      id: "works",
      scene: "device-trio",
      kicker: "To nie koncept",
      title: "*Gotowe* i uruchomione",
      stats: [
        { value: "3", label: "platformy: web, iOS, Android" },
        { value: "3", label: "węzły łańcucha (2-z-3)" },
        { value: "1000", label: "transakcji w bloku w testach" },
        { value: "100%", label: "wyniku weryfikowalne publicznie" }
      ],
      durationSec: 60,
      notes: "Żywy produkt: web + iOS + Android, eksplorer, łańcuch 3-węzłowy, przebadana skala."
    },
    {
      id: "scale",
      kicker: "Skala i wydajność",
      title: "*Skala* i wydajność",
      lead: "Przebadaliśmy, ile system realnie uniesie i jak go skalować.",
      stats: [
        { value: "1000", label: "transakcji naraz — zbieżnie na 3 węzłach" },
        { value: "~15%", label: "obciążenia CPU przy piku" },
        { value: "liniowo", label: "skaluje się z liczbą serwerów" }
      ],
      durationSec: 55,
      notes: "Napływ buforowany (baza + kolejka), łańcuch pakuje go miarowo. Skala liniowa."
    },
    {
      id: "proof",
      scene: "verify-lights",
      kicker: "Dowód",
      title: "Dowód: *podpisany* protokół",
      lead: "Wynik trafia do podpisanego dokumentu z pieczęcią blockchaina — i każdy może go zweryfikować.",
      bullets: ["Protokół PDF dla organizatora i głosujących.", "Jedno kliknięcie: wynik zgodny z łańcuchem albo wykryta zmiana."],
      durationSec: 55,
      notes: "Materialny dowód: podpisany protokół + publiczna weryfikacja."
    },

    // ── Rozdział VI ──
    { id: "div6", divider: true, tone: "brand", chapterNo: "VI", title: "Następne *kroki*", lead: "Plan i propozycja współpracy.", notes: "Domknięcie." },
    { id: "roadmap", scene: "roadmap", kicker: "Roadmapa", title: "Co dalej — *roadmapa*", lead: "Łańcuch i ZKP gotowe; przed nami publiczne API i tożsamość.", durationSec: 50, notes: "Pokaż, że fundament działa, a rozwój idzie w stronę otwarcia dla samorządów." },
    {
      id: "foundation",
      scene: "institution",
      kicker: "Dlaczego my",
      title: "Polskie, niezależne, *jawne*",
      bullets: ["Pełna kontrola nad krytyczną infrastrukturą wyborczą.", "Otwarty kod i publiczna weryfikowalność.", "Misja Fundacji: wiarygodne, dostępne głosowania."],
      durationSec: 60,
      notes: "Racja stanu: suwerenność = bezpieczeństwo i wiarygodność instytucji."
    },
    {
      id: "cta",
      tone: "brand",
      scene: "launch",
      kicker: "Następny krok",
      title: "Zróbmy *pilotaż*",
      lead: "Wybierzmy jedno głosowanie lub konsultację i pokażmy efekt na realnym przykładzie, w pełni pod polską kontrolą.",
      cta: "kontakt@potrafiszpolsko.pl · lubbezposrednio.pl",
      durationSec: 45,
      notes: "Zamknięcie z konkretną, niskoryzykowną propozycją pilotażu. Zostaw kontakt."
    }
  ]
};
